/* eslint-disable @typescript-eslint/naming-convention */
import { Injectable } from '@angular/core';

import { NeoFacetRequestApiModel } from '@api/mentions/model/neo-facet-request-api.model';
import { NeoFacetDateFieldApiEnum } from '@api/mentions/model/neo-facet-date-field-api.enum';
import { NeoFacetTermFieldApiEnum } from '@api/mentions/enum/neo-facet-term-field-api.enum';
import { BucketFunctionApiEnum } from '@api/mentions/enum/bucket-function-api.enum';
import { MentionSearchFacetTypesEnum } from '@api/mentions/enum/mention-search-facet-types.enum';
import { SortOrderEnum } from '@api/shared/enums/sort-order.enum';
import { SearchCriteriaSortFieldEnum } from '@api/mentions/enum/search-criteria-sort-field.enum';
import { MentionsSearchResponseModel } from '@api/mentions/model/mentions-search-response.model';

import { LocalizationService } from '@Core/Localization/services/localization.service';

import { ReportSeriesFacetGeneratorEnum } from '@Common/Reports/models/report-series-facet-generator.enum';
import { ReportsNeoFacetKeys } from '@Common/Reports/enums/reports-neo-facet-keys.enum';
import { DataPointModel } from '@Common/Charts/models/data-point.model';
import { ReportsSeriesDataMapperService } from '@Common/Reports/services/reports-series-data-mapper.service';
import { ChartMetricsEnum } from '@Common/Reports/enums/chart-metrics.enum';
import { Mention } from '@Common/Mentions/models/mention';
import { ReportSeriesModel } from '@Common/Reports/models/report-series.model';
import { JournodexRegionsService } from '@Common/Journodex/services/journodex-regions.service';

import { MediaTypeEnum } from '@Common/Basic/enums/media-type.enum';
import { SocialSeriesNeoFacetKeysEnum } from '@Social/Intelligence/enums/social-series-neo-facet-keys.enum';

import { IndividualReportSeriesConfig } from '@Monitor/Core/services/monitoring-dashboard-search.service';
import { MonitoringRoutingService } from '@Monitor/Core/services/monitoring-routing.service';
import { MediaTypeModel } from '@Monitor/Shared/models/media-type.model';
import { MADChartsService } from '@Monitor/Shared/services/mad-charts.service';
import { MentionSearchCriteriaModel } from '@Monitor/Shared/models/mention-search-criteria.model';
import { MADSocialSeriesTypesEnum } from '@Monitor/Shared/enums/mad-series-types.enum';

@Injectable()
export class MADSocialService extends MADChartsService {

    readonly dateBulletFormat: string = 'YYYY/MM/DD hh:mm';
    readonly MADSocialSeriesConfig: IndividualReportSeriesConfig[] = [
        // Cards
        {
            type: MADSocialSeriesTypesEnum.totalVolumeCard,
            label: `Monitor.MAD.social.${MADSocialSeriesTypesEnum.totalVolumeCard}.label`,
            facetGenerator: () => {
                return {
                    facet: socialTrendNeoFacetRequest,
                    identifier: ReportSeriesFacetGeneratorEnum.neoFacet
                };
            },
            dataMapper: (response) => {
                const neoFacet = response.neoFacetResults.find(facet => facet.key === ReportsNeoFacetKeys.overtimeFacet);
                const count = neoFacet?.buckets.reduce((previousValue, bucket) => previousValue + (bucket.count || 0), 0);
                return [{
                    count: count
                }];
            }
        },
        {
            type: MADSocialSeriesTypesEnum.totalVolumeCardPreviousPeriod,
            isPreviousPeriod: true,
            facetGenerator: () => {
                return {
                    facet: socialTrendNeoFacetRequest,
                    identifier: ReportSeriesFacetGeneratorEnum.neoFacet
                };
            },
            dataMapper: (response) => {
                const neoFacet = response.neoFacetResults.find(facet => facet.key === ReportsNeoFacetKeys.overtimeFacet);
                const count = neoFacet?.buckets.reduce((previousValue, bucket) => previousValue + (bucket.count || 0), 0);
                return [{
                    count: count
                }];
            }
        },
        {
            type: MADSocialSeriesTypesEnum.totalImpressionCard,
            label: `Monitor.MAD.social.${MADSocialSeriesTypesEnum.totalImpressionCard}.label`,
            facetGenerator: () => {
                return {
                    facet: socialTrendNeoFacetRequest,
                    identifier: ReportSeriesFacetGeneratorEnum.neoFacet
                };
            },
            dataMapper: (response) => {
                const neoFacet = response.neoFacetResults.find(facet => facet.key === ReportsNeoFacetKeys.overtimeFacet);
                const count = neoFacet?.buckets.reduce((previousValue, bucket) =>
                    previousValue + (bucket.buckets[ReportsNeoFacetKeys.impressionsOverTimeFacet][0]?.count || 0), 0);

                return [{
                    count: count
                }];
            }
        },
        {
            type: MADSocialSeriesTypesEnum.totalImpressionCardPreviousPeriod,
            isPreviousPeriod: true,
            facetGenerator: () => {
                return {
                    facet: socialTrendNeoFacetRequest,
                    identifier: ReportSeriesFacetGeneratorEnum.neoFacet
                };
            },
            dataMapper: (response) => {
                const neoFacet = response.neoFacetResults.find(facet => facet.key === ReportsNeoFacetKeys.overtimeFacet);
                const count = neoFacet?.buckets.reduce((previousValue, bucket) =>
                    previousValue + (bucket.buckets[ReportsNeoFacetKeys.impressionsOverTimeFacet][0]?.count || 0), 0);

                return [{
                    count: count
                }];
            }
        },
        {
            type: MADSocialSeriesTypesEnum.totalEngagementCard,
            label: `Monitor.MAD.social.${MADSocialSeriesTypesEnum.totalEngagementCard}.label`,
            facetGenerator: () => {
                return {
                    facet: socialTrendNeoFacetRequest,
                    identifier: ReportSeriesFacetGeneratorEnum.neoFacet
                };
            },
            dataMapper: (response) => {
                const neoFacet = response.neoFacetResults.find(facet => facet.key === ReportsNeoFacetKeys.overtimeFacet);
                const count = neoFacet?.buckets.reduce((previousValue, bucket) =>
                    previousValue + (bucket.buckets[ReportsNeoFacetKeys.engagementsOverTimeFacet][0]?.count || 0), 0);

                return [{
                    count: count
                }];
            }
        },
        {
            type: MADSocialSeriesTypesEnum.totalEngagementCardPreviousPeriod,
            isPreviousPeriod: true,
            facetGenerator: () => {
                return {
                    facet: socialTrendNeoFacetRequest,
                    identifier: ReportSeriesFacetGeneratorEnum.neoFacet
                };
            },
            dataMapper: (response) => {
                const neoFacet = response.neoFacetResults.find(facet => facet.key === ReportsNeoFacetKeys.overtimeFacet);
                const count = neoFacet?.buckets.reduce((previousValue, bucket) =>
                    previousValue + (bucket.buckets[ReportsNeoFacetKeys.engagementsOverTimeFacet][0]?.count || 0), 0);

                return [{
                    count: count
                }];
            }
        },

        // Summary By Social Network
        {
            type: MADSocialSeriesTypesEnum.summaryTrend,
            metrics: [ChartMetricsEnum.volumeOfMentions, ChartMetricsEnum.IMPRESSIONS, ChartMetricsEnum.ENGAGEMENTS],
            facetGenerator: () => {
                const facetRequest = JSON.parse(JSON.stringify(socialTrendNeoFacetRequest));
                return {
                    facet: facetRequest,
                    identifier: ReportSeriesFacetGeneratorEnum.neoFacet
                };
            },
            dataMapper: (response, request, series) => {
                return this.reportsSeriesDataMapperService.mapDateDataFromNeoFacet(response, series);
            }
        },
        {
            type: MADSocialSeriesTypesEnum.summaryTotalVolumeCard,
            label: `Monitor.MAD.social.${MADSocialSeriesTypesEnum.summaryTotalVolumeCard}.label`,
            facetGenerator: () => {
                return {
                    facet: socialTrendNeoFacetRequest,
                    identifier: ReportSeriesFacetGeneratorEnum.neoFacet
                };
            },
            dataMapper: (response) => {
                const neoFacet = response.neoFacetResults.find(facet => facet.key === ReportsNeoFacetKeys.overtimeFacet);
                const count = neoFacet?.buckets.reduce((previousValue, bucket) => previousValue + (bucket.count || 0), 0);
                return [{
                    count: count
                }];
            }
        },
        {
            type: MADSocialSeriesTypesEnum.summaryTotalVolumeCardPreviousPeriod,
            isPreviousPeriod: true,
            facetGenerator: () => {
                return {
                    facet: socialTrendNeoFacetRequest,
                    identifier: ReportSeriesFacetGeneratorEnum.neoFacet
                };
            },
            dataMapper: (response) => {
                const neoFacet = response.neoFacetResults.find(facet => facet.key === ReportsNeoFacetKeys.overtimeFacet);
                const count = neoFacet?.buckets.reduce((previousValue, bucket) => previousValue + (bucket.count || 0), 0);
                return [{
                    count: count
                }];
            }
        },
        {
            type: MADSocialSeriesTypesEnum.summaryTotalImpressionCard,
            label: `Monitor.MAD.social.${MADSocialSeriesTypesEnum.summaryTotalImpressionCard}.label`,
            facetGenerator: () => {
                return {
                    facet: socialTrendNeoFacetRequest,
                    identifier: ReportSeriesFacetGeneratorEnum.neoFacet
                };
            },
            dataMapper: (response) => {
                const neoFacet = response.neoFacetResults.find(facet => facet.key === ReportsNeoFacetKeys.overtimeFacet);
                const count = neoFacet?.buckets.reduce((previousValue, bucket) =>
                    previousValue + (bucket.buckets[ReportsNeoFacetKeys.impressionsOverTimeFacet][0]?.count || 0), 0);

                return [{
                    count: count
                }];
            }
        },
        {
            type: MADSocialSeriesTypesEnum.summaryTotalImpressionCardPreviousPeriod,
            isPreviousPeriod: true,
            facetGenerator: () => {
                return {
                    facet: socialTrendNeoFacetRequest,
                    identifier: ReportSeriesFacetGeneratorEnum.neoFacet
                };
            },
            dataMapper: (response) => {
                const neoFacet = response.neoFacetResults.find(facet => facet.key === ReportsNeoFacetKeys.overtimeFacet);
                const count = neoFacet?.buckets.reduce((previousValue, bucket) =>
                    previousValue + (bucket.buckets[ReportsNeoFacetKeys.impressionsOverTimeFacet][0]?.count || 0), 0);

                return [{
                    count: count
                }];
            }
        },
        {
            type: MADSocialSeriesTypesEnum.summaryTotalEngagementCard,
            label: `Monitor.MAD.social.${MADSocialSeriesTypesEnum.summaryTotalEngagementCard}.label`,
            facetGenerator: () => {
                return {
                    facet: socialTrendNeoFacetRequest,
                    identifier: ReportSeriesFacetGeneratorEnum.neoFacet
                };
            },
            dataMapper: (response) => {
                const neoFacet = response.neoFacetResults.find(facet => facet.key === ReportsNeoFacetKeys.overtimeFacet);
                const count = neoFacet?.buckets.reduce((previousValue, bucket) =>
                    previousValue + (bucket.buckets[ReportsNeoFacetKeys.engagementsOverTimeFacet][0]?.count || 0), 0);

                return [{
                    count: count
                }];
            }
        },
        {
            type: MADSocialSeriesTypesEnum.summaryTotalEngagementCardPreviousPeriod,
            isPreviousPeriod: true,
            facetGenerator: () => {
                return {
                    facet: socialTrendNeoFacetRequest,
                    identifier: ReportSeriesFacetGeneratorEnum.neoFacet
                };
            },
            dataMapper: (response) => {
                const neoFacet = response.neoFacetResults.find(facet => facet.key === ReportsNeoFacetKeys.overtimeFacet);
                const count = neoFacet?.buckets.reduce((previousValue, bucket) =>
                    previousValue + (bucket.buckets[ReportsNeoFacetKeys.engagementsOverTimeFacet][0]?.count || 0), 0);

                return [{
                    count: count
                }];
            }
        },

        // Social Network Comparison
        {
            type: MADSocialSeriesTypesEnum.totalVolumePie,
            facetGenerator: () => {
                return {
                    facet: mediaTypeNeoFacetRequest,
                    identifier: ReportSeriesFacetGeneratorEnum.neoFacet
                };
            },
            dataMapper: (response, request, series) => {
                return this.setDataMapperVolumePie(response, series);
            }
        },
        {
            type: MADSocialSeriesTypesEnum.totalVolumePiePreviousPeriod,
            label: 'Social.intelligence.themeAnalytics.previousPeriod',
            isPreviousPeriod: true,
            facetGenerator: () => {
                return {
                    facet: mediaTypeNeoFacetRequest,
                    identifier: ReportSeriesFacetGeneratorEnum.neoFacet
                };
            },
            dataMapper: (response, request, series) => {
                return this.setDataMapperVolumePie(response, series);
            }
        },
        {
            type: MADSocialSeriesTypesEnum.totalImpressionPie,
            facetGenerator: () => {
                return {
                    facet: mediaTypeNeoFacetRequest,
                    identifier: ReportSeriesFacetGeneratorEnum.neoFacet
                };
            },
            dataMapper: (response, request, series) => {
                return this.setDataMapperImpressionsPie(response, series);
            }
        },
        {
            type: MADSocialSeriesTypesEnum.totalImpressionPiePreviousPeriod,
            label: 'Social.intelligence.themeAnalytics.previousPeriod',
            isPreviousPeriod: true,
            facetGenerator: () => {
                return {
                    facet: mediaTypeNeoFacetRequest,
                    identifier: ReportSeriesFacetGeneratorEnum.neoFacet
                };
            },
            dataMapper: (response, request, series) => {
                return this.setDataMapperImpressionsPie(response, series);
            }
        },
        {
            type: MADSocialSeriesTypesEnum.totalEngagementPie,
            facetGenerator: () => {
                return {
                    facet: mediaTypeNeoFacetRequest,
                    identifier: ReportSeriesFacetGeneratorEnum.neoFacet
                };
            },
            dataMapper: (response, request, series) => {
                return this.setDataMapperEngagementsPie(response, series);
            }
        },
        {
            type: MADSocialSeriesTypesEnum.totalEngagementPiePreviousPeriod,
            label: 'Social.intelligence.themeAnalytics.previousPeriod',
            isPreviousPeriod: true,
            facetGenerator: () => {
                return {
                    facet: mediaTypeNeoFacetRequest,
                    identifier: ReportSeriesFacetGeneratorEnum.neoFacet
                };
            },
            dataMapper: (response, request, series) => {
                return this.setDataMapperEngagementsPie(response, series);
            }
        },

        // Social Sentiment Trend
        {
            type: MADSocialSeriesTypesEnum.sentimentTrend,
            facetGenerator: () => {
                return {
                    facet: sentimentTrendNeoFacet,
                    identifier: ReportSeriesFacetGeneratorEnum.neoFacet
                };
            },
            dataMapper: (response, request, series) => {
                return this.reportsSeriesDataMapperService.mapSentimentDateDataFromNeoFacet(response, series);
            },
        },

        // Top Terms
        {
            type: MADSocialSeriesTypesEnum.topTerms,
            facetGenerator: () => {
                return {
                    facet: [
                        // Initial Facet hashtags
                        MentionSearchFacetTypesEnum.hashtags,
                    ],
                    identifier: ReportSeriesFacetGeneratorEnum.facet
                };
            },
            dataMapper(response, request, series) {
                const values = response.facetResults
                    .find(facet => facet.field === MentionSearchFacetTypesEnum.hashtags)
                    ?.values.slice(0, 5) ?? [];
                return values.map((val, i) => {
                    // Add hashtag to filter
                    const filterOverrides = series.mentions.filters.searchCriteria.clone();
                    filterOverrides.query.hashtags.include.push(val.value);

                    return {
                        id: i + '',
                        label: val.value,
                        count: val.count,
                        'viewMentionUrl': series.getSocialSeriesUrl(filterOverrides)
                    } as DataPointModel;
                });
            },
        },

        // Top Posts
        {
            type: MADSocialSeriesTypesEnum.topPosts,
            mentionRequestGenerator: () => {
                return {
                    sortOrder: SortOrderEnum.descending,
                    sortField: SearchCriteriaSortFieldEnum.audience_reach,
                    pageSize: 5
                };
            },
            mentionDataMapper: (response) => {
                return response.mentions.map(apiMention => new Mention(apiMention));
            }
        },
    ];

    constructor(
        public localizationService: LocalizationService,
        public reportsSeriesDataMapperService: ReportsSeriesDataMapperService,
        public routingService: MonitoringRoutingService,
        public journodexSearchService: JournodexRegionsService
    ) {
        super(routingService, localizationService, journodexSearchService);
        this.setChartConfig(this.MADSocialSeriesConfig);
    }

    setDataMapperVolumePie(response: MentionsSearchResponseModel, series?: ReportSeriesModel): DataPointModel[] {
        const neoFacet = response.neoFacetResults.find(facet => facet.key === SocialSeriesNeoFacetKeysEnum.mediaTypeFacetGroup);
        if (!neoFacet) {
            return [];
        }
        return neoFacet?.buckets.map(bucket => {
            const filterOverrides = new MentionSearchCriteriaModel();

            // map 'TWEET' for backwards compatibility
            if (bucket.termValue === 'TWEET') {
                bucket.termValue = MediaTypeEnum.TWITTER;
            }

            filterOverrides.query.includedMediaTypes = [new MediaTypeModel(bucket.termValue as MediaTypeEnum)];

            return <DataPointModel>{
                id: bucket.termValue,
                label: this.localizationService.transform(`MediaType.${bucket.termValue as MediaTypeEnum}`),
                xValue: bucket.count,
                viewMentionUrl: series.getSocialSeriesUrl(filterOverrides, null, true)
            };
        });
    }

    setDataMapperImpressionsPie(response: MentionsSearchResponseModel, series?: ReportSeriesModel): DataPointModel[] {
        const neoFacet = response.neoFacetResults.find(facet => facet.key === SocialSeriesNeoFacetKeysEnum.mediaTypeFacetGroup);
        if (!neoFacet) {
            return [];
        }
        return neoFacet?.buckets.map(bucket => {
            const filterOverrides = new MentionSearchCriteriaModel();

            // map 'TWEET' for backwards compatibility
            if (bucket.termValue === 'TWEET') {
                bucket.termValue = MediaTypeEnum.TWITTER;
            }

            filterOverrides.query.includedMediaTypes = [new MediaTypeModel(bucket.termValue as MediaTypeEnum)];
            return <DataPointModel>{
                id: bucket.termValue,
                label: this.localizationService.transform(`MediaType.${bucket.termValue as MediaTypeEnum}`),
                xValue: bucket.buckets[SocialSeriesNeoFacetKeysEnum.mediaTypeImpressions][0].count,
                viewMentionUrl: series.getSocialSeriesUrl(filterOverrides, null, true)
            };
        });
    }

    setDataMapperEngagementsPie(response: MentionsSearchResponseModel, series?: ReportSeriesModel): DataPointModel[] {
        const neoFacet = response.neoFacetResults.find(facet => facet.key === SocialSeriesNeoFacetKeysEnum.mediaTypeFacetGroup);
        if (!neoFacet) {
            return [];
        }
        return neoFacet?.buckets.map(bucket => {
            const filterOverrides = new MentionSearchCriteriaModel();

            // map 'TWEET' for backwards compatibility
            if (bucket.termValue === 'TWEET') {
                bucket.termValue = MediaTypeEnum.TWITTER;
            }

            filterOverrides.query.includedMediaTypes = [new MediaTypeModel(bucket.termValue as MediaTypeEnum)];
            filterOverrides.sortField = SearchCriteriaSortFieldEnum.engagement_total;

            return <DataPointModel>{
                id: bucket.termValue,
                label: this.localizationService.transform(`MediaType.${bucket.termValue as MediaTypeEnum}`),
                xValue: bucket.buckets[SocialSeriesNeoFacetKeysEnum.mediaTypeEngagements][0].count,
                viewMentionUrl: series.getSocialSeriesUrl(filterOverrides, null, true)
            };
        });
    }
}

const sentimentTrendNeoFacet: NeoFacetRequestApiModel = {
    dateField: NeoFacetDateFieldApiEnum.CREATED_AT,
    key: ReportsNeoFacetKeys.sentimentOverTime,
    plainBucketRequests: [
        {
            key: ReportsNeoFacetKeys.sentimentTrend,
            field: NeoFacetTermFieldApiEnum.SENTIMENT
        }
    ]
};

const mediaTypeNeoFacetRequest: NeoFacetRequestApiModel = {
    termField: NeoFacetTermFieldApiEnum.MEDIA_TYPE,
    key: SocialSeriesNeoFacetKeysEnum.mediaTypeFacetGroup,
    aggregateBucketRequests: [
        {
            key: SocialSeriesNeoFacetKeysEnum.mediaTypeEngagements,
            field: NeoFacetTermFieldApiEnum.ENGAGEMENT_TOTAL,
            function: BucketFunctionApiEnum.INDIVIDUAL_SUM
        },
        {
            key: SocialSeriesNeoFacetKeysEnum.mediaTypeImpressions,
            field: NeoFacetTermFieldApiEnum.AUDIENCE_REACH,
            function: BucketFunctionApiEnum.INDIVIDUAL_SUM
        },
    ]
};
const socialTrendNeoFacetRequest: NeoFacetRequestApiModel = {
    dateField: NeoFacetDateFieldApiEnum.CREATED_AT,
    key: ReportsNeoFacetKeys.overtimeFacet,
    aggregateBucketRequests: [
        {
            key: ReportsNeoFacetKeys.impressionsOverTimeFacet,
            field: NeoFacetTermFieldApiEnum.AUDIENCE_REACH,
            function: BucketFunctionApiEnum.INDIVIDUAL_SUM
        },
        {
            key: ReportsNeoFacetKeys.aveOverTimeFacet,
            field: NeoFacetTermFieldApiEnum.AD_VALUE_USD,
            function: BucketFunctionApiEnum.INDIVIDUAL_SUM
        },
        {
            key: ReportsNeoFacetKeys.engagementsOverTimeFacet,
            field: NeoFacetTermFieldApiEnum.ENGAGEMENT_TOTAL,
            function: BucketFunctionApiEnum.INDIVIDUAL_SUM
        },
    ],
};

